/**
 * <s-lab-tabs>
 * Line switcher for several Color Labs (blocks/color-lab.liquid) on the homepage.
 * - [data-lab-tab] buttons (role="tab", aria-controls → panel id) pick one panel; the others get `hidden`.
 * - Arrow keys / Home / End move between tabs (roving tabindex).
 * - Mirrors the active panel's --lab colour (set by s-color-lab) onto the host, so the tab bar tints with it.
 * Without JS every panel stays visible.
 */
class SLabTabs extends HTMLElement {
  #ac = null;
  #observer = null;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    this.tabs = [...this.querySelectorAll('[data-lab-tab]')];
    this.panels = this.tabs.map((tab) => document.getElementById(tab.getAttribute('aria-controls')));
    if (this.tabs.length === 0) return;

    const start = Math.max(0, this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true'));
    this.#select(start, false, false);

    this.addEventListener('click', (event) => {
      const tab = event.target instanceof Element ? event.target.closest('[data-lab-tab]') : null;
      if (tab) this.#select(this.tabs.indexOf(tab), false);
    }, { signal });

    this.addEventListener('keydown', (event) => {
      if (!(event.target instanceof Element) || !event.target.matches('[data-lab-tab]')) return;
      const current = this.tabs.indexOf(event.target);
      const rtl = getComputedStyle(this).direction === 'rtl';
      const last = this.tabs.length - 1;
      let next = null;
      if (event.key === 'ArrowRight') next = rtl ? current - 1 : current + 1;
      if (event.key === 'ArrowLeft') next = rtl ? current + 1 : current - 1;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = last;
      if (next === null) return;
      event.preventDefault();
      this.#select((next + this.tabs.length) % this.tabs.length, true);
    }, { signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
  }

  #select(index, focus, scroll = true) {
    this.tabs.forEach((tab, i) => {
      const active = i === index;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
      if (this.panels[i]) this.panels[i].hidden = !active;
    });
    const tab = this.tabs[index];
    if (focus) tab.focus();
    if (scroll) {
      const list = tab.parentElement;
      if (list && list.scrollWidth > list.clientWidth) list.scrollTo({ left: tab.offsetLeft - list.offsetLeft - 16 });
    }
    this.#watch(this.panels[index]?.querySelector('s-color-lab'));
  }

  #watch(lab) {
    this.#observer?.disconnect();
    if (!lab) return;
    const sync = () => {
      const color = lab.style.getPropertyValue('--lab');
      if (color) this.style.setProperty('--lab', color);
    };
    sync();
    this.#observer = new MutationObserver(sync);
    this.#observer.observe(lab, { attributes: true, attributeFilter: ['style'] });
  }
}

if (!customElements.get('s-lab-tabs')) customElements.define('s-lab-tabs', SLabTabs);
